import { Node, Edge } from "@xyflow/react";
import dagre from "dagre";

export interface ValueChainNodeData extends Record<string, unknown> {
    entity: {
        id: number;
        title: string;
        type: "ingredient" | "intermediate_product" | "product";
        isIntermediate?: boolean;
        ownedByName?: string | null;
        quantity?: number | null;
    };
    isCollapsed: boolean;
    childrenCount: number;
    depth: number;
    layoutDirection?: "TB" | "LR";
}

export type ValueChainNode = Node<ValueChainNodeData>;
export type ValueChainEdge = Edge;

export interface ValueChainTreeState {
    nodes: ValueChainNode[];
    edges: ValueChainEdge[];
    collapsedNodes: Set<string>;
}

interface ChainEntity {
    id: number;
    title: string;
    type: string;
    isIntermediate?: boolean;
    quantity?: number | null;
    ownedByName?: string | null;
    children?: ChainEntity[];
    depth?: number;
}

/**
 * Build value chain tree from API data
 */
export const buildValueChainFromData = (
    upstream: ChainEntity[],
    downstream: ChainEntity[],
    rootEntity: { id: number; title: string; type: string; isIntermediate?: boolean; ownedByName?: string | null },
    collapsedNodes: Set<string> = new Set(),
): ValueChainTreeState => {
    const nodes: ValueChainNode[] = [];
    const edges: ValueChainEdge[] = [];

    // Create root node
    const rootId = `${rootEntity.type}-${rootEntity.id}`;
    nodes.push({
        id: rootId,
        type: "valueChainNode",
        position: { x: 0, y: 0 },
        data: {
            entity: {
                id: rootEntity.id,
                title: rootEntity.title,
                type: rootEntity.type as "ingredient" | "intermediate_product" | "product",
                isIntermediate: rootEntity.isIntermediate,
                ownedByName: rootEntity.ownedByName,
            },
            isCollapsed: collapsedNodes.has(rootId),
            childrenCount: upstream.length + downstream.length,
            depth: 0,
        },
    });

    // Process upstream (sources)
    upstream.forEach((entity) => {
        processUpstreamEntity(entity, rootId, nodes, edges, collapsedNodes);
    });

    // Process downstream (destinations)
    downstream.forEach((entity) => {
        processDownstreamEntity(entity, rootId, nodes, edges, collapsedNodes);
    });

    return { nodes, edges, collapsedNodes };
};

function processUpstreamEntity(
    entity: ChainEntity,
    parentId: string,
    nodes: ValueChainNode[],
    edges: ValueChainEdge[],
    collapsedNodes: Set<string>,
    depth: number = 1,
): void {
    const nodeId = `${entity.type}-${entity.id}`;
    const isCollapsed = collapsedNodes.has(nodeId);

    // Add node if it doesn't exist
    if (!nodes.find((n) => n.id === nodeId)) {
        nodes.push({
            id: nodeId,
            type: "valueChainNode",
            position: { x: 0, y: 0 },
            data: {
                entity: {
                    id: entity.id,
                    title: entity.title,
                    type: entity.type as "ingredient" | "intermediate_product" | "product",
                    isIntermediate: entity.isIntermediate,
                    ownedByName: entity.ownedByName,
                    quantity: entity.quantity,
                },
                isCollapsed,
                childrenCount: entity.children?.length || 0,
                depth,
            },
        });
    }

    // Add edge from source to parent (upstream flows TO the parent)
    const edgeId = `${nodeId}-${parentId}`;
    if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
            id: edgeId,
            source: nodeId,
            target: parentId,
            type: "smoothstep",
            label: entity.quantity ? `${entity.quantity} kg` : undefined,
            animated: false,
        });
    }

    // Process children if not collapsed
    if (!isCollapsed && entity.children && entity.children.length > 0) {
        entity.children.forEach((child) => {
            processUpstreamEntity(child, nodeId, nodes, edges, collapsedNodes, depth + 1);
        });
    }
}

function processDownstreamEntity(
    entity: ChainEntity,
    parentId: string,
    nodes: ValueChainNode[],
    edges: ValueChainEdge[],
    collapsedNodes: Set<string>,
    depth: number = 1,
): void {
    const nodeId = `${entity.type}-${entity.id}`;
    const isCollapsed = collapsedNodes.has(nodeId);

    // Add node if it doesn't exist
    if (!nodes.find((n) => n.id === nodeId)) {
        nodes.push({
            id: nodeId,
            type: "valueChainNode",
            position: { x: 0, y: 0 },
            data: {
                entity: {
                    id: entity.id,
                    title: entity.title,
                    type: entity.type as "ingredient" | "intermediate_product" | "product",
                    isIntermediate: entity.isIntermediate,
                    ownedByName: entity.ownedByName,
                },
                isCollapsed,
                childrenCount: entity.children?.length || 0,
                depth,
            },
        });
    }

    // Add edge from parent to destination (downstream flows FROM the parent)
    const edgeId = `${parentId}-${nodeId}`;
    if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
            id: edgeId,
            source: parentId,
            target: nodeId,
            type: "smoothstep",
            animated: false,
        });
    }

    // Process children if not collapsed
    if (!isCollapsed && entity.children && entity.children.length > 0) {
        entity.children.forEach((child) => {
            processDownstreamEntity(child, nodeId, nodes, edges, collapsedNodes, depth + 1);
        });
    }
}

/**
 * Calculate layout using dagre
 */
export const calculateValueChainLayout = (
    nodes: ValueChainNode[],
    edges: ValueChainEdge[],
    direction: "TB" | "LR" = "TB",
): { nodes: ValueChainNode[]; edges: ValueChainEdge[] } => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));

    dagreGraph.setGraph({
        rankdir: direction,
        nodesep: 80,
        ranksep: 120,
        edgesep: 20,
        marginx: 40,
        marginy: 40,
    });

    // Add nodes
    nodes.forEach((node) => {
        dagreGraph.setNode(node.id, {
            width: 220,
            height: 100,
        });
    });

    // Add edges
    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    // Calculate layout
    dagre.layout(dagreGraph);

    // Apply calculated positions and add layoutDirection to node data
    const layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        return {
            ...node,
            position: {
                x: nodeWithPosition.x - nodeWithPosition.width / 2,
                y: nodeWithPosition.y - nodeWithPosition.height / 2,
            },
            data: {
                ...node.data,
                layoutDirection: direction,
            },
        };
    });

    // Update edge styles based on direction
    const layoutedEdges = edges.map((edge) => ({
        ...edge,
        type: "smoothstep",
        // Ensure edges follow the layout direction
        style: {
            strokeWidth: 2,
        },
        // Use appropriate pathOptions for horizontal/vertical layout
        pathOptions: direction === "LR" ? { borderRadius: 8 } : { borderRadius: 8 },
    }));

    return { nodes: layoutedNodes, edges: layoutedEdges };
};

/**
 * Toggle collapse state of a node
 */
export const toggleNodeCollapse = (nodeId: string, currentCollapsedNodes: Set<string>): Set<string> => {
    const newCollapsedNodes = new Set(currentCollapsedNodes);

    if (newCollapsedNodes.has(nodeId)) {
        newCollapsedNodes.delete(nodeId);
    } else {
        newCollapsedNodes.add(nodeId);
    }

    return newCollapsedNodes;
};

/**
 * Collapse all nodes
 */
export const collapseAllNodes = (nodes: ValueChainNode[]): Set<string> => {
    const collapsed = new Set<string>();
    nodes.forEach((node) => {
        if (node.data.childrenCount > 0) {
            collapsed.add(node.id);
        }
    });
    return collapsed;
};

/**
 * Expand all nodes
 */
export const expandAllNodes = (): Set<string> => {
    return new Set();
};
